import { Button, message } from "antd";
import type { CustomerInvoiceHeader } from "@zhihaoscm/entity";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { invoiceService } from "@/api/invoiceService";
import { Breadcrumb, ProTable, ZhMessageBox, type ProColumn } from "@/components/common";

const headerTypes = [
    { label: "企业", value: 1 },
    { label: "个人或事业单位", value: 2 }
];
const invoiceTypes = [
    { label: "普通发票", value: 1 },
    { label: "增值税专用发票", value: 2 }
];

/** 发票抬头列表保留旧版的查询、新增、修改、删除及默认抬头操作。 */
export function InvoiceListPage() {
    const navigate = useNavigate();
    const [revision, setRevision] = useState(0);
    const [deleteId, setDeleteId] = useState<number>();
    /** 删除成功后刷新当前列表，避免残留已删除记录。 */
    const remove = async () => {
        if (deleteId == null) return;
        await invoiceService.deleteInvoice(deleteId);
        setDeleteId(undefined);
        setRevision(value => value + 1);
    };
    /** 设置默认时提交完整抬头，保持与旧接口的 PUT 契约一致。 */
    const setDefault = async (row: CustomerInvoiceHeader) => {
        const form = {
            headerType: row.headerType,
            invoiceType: row.invoiceType,
            name: row.name,
            taxpayerNo: row.taxpayerNo,
            mobile: row.mobile,
            address: row.address,
            bank: row.bank,
            account: row.account,
            isDefault: 1
        };
        await invoiceService.setDefault(row.id, form);
        setRevision(value => value + 1);
        void message.success("设置成功");
    };
    const columns = useMemo<ProColumn<CustomerInvoiceHeader>[]>(
        () => [
            {
                key: "headerType",
                title: "抬头类型",
                minWidth: 114,
                enum: headerTypes,
                search: {
                    key: "headerType",
                    type: "select",
                    placeholder: "请选择抬头类型",
                    options: headerTypes,
                    width: 80,
                    span: 3,
                    order: 2
                }
            },
            {
                key: "invoiceType",
                title: "发票类型",
                minWidth: 114,
                enum: invoiceTypes,
                search: {
                    key: "invoiceType",
                    type: "select",
                    placeholder: "请选择发票类型",
                    options: invoiceTypes,
                    width: 80,
                    span: 3,
                    order: 3
                },
                render: (_, row) =>
                    row.headerType === 2
                        ? "--"
                        : (invoiceTypes.find(item => item.value === row.invoiceType)?.label ?? "--")
            },
            {
                key: "name",
                title: "名称",
                minWidth: 142,
                search: { key: "name", type: "input", placeholder: "请输入名称", width: 120, span: 5, order: 1 }
            },
            { key: "taxpayerNo", title: "纳税人识别号", minWidth: 142 },
            { key: "address", title: "地址", minWidth: 142 },
            { key: "mobile", title: "电话", minWidth: 142 },
            { key: "bank", title: "开户行", minWidth: 142 },
            { key: "account", title: "账号", minWidth: 142 },
            {
                key: "operation",
                title: "操作",
                width: 170,
                fixed: "right",
                render: (_, row) => (
                    <div className="invoice-list-actions">
                        <Button type="link" onClick={() => navigate(`/me/invoice/add?id=${row.id}`)}>
                            修改
                        </Button>
                        <Button type="link" onClick={() => setDeleteId(row.id)}>
                            删除
                        </Button>
                        {row.isDefault ? (
                            <span className="invoice-default">默认抬头</span>
                        ) : (
                            <Button type="link" onClick={() => void setDefault(row)}>
                                设为默认
                            </Button>
                        )}
                    </div>
                )
            }
        ],
        [navigate]
    );
    /** 分页参数原样传递给发票服务。 */
    const request = useMemo(
        () => async (params: Record<string, unknown>) => invoiceService.getInvoiceList(params),
        [revision]
    );
    return (
        <section className="invoice-header-page section-card">
            <Breadcrumb routes={[{ name: "账号管理" }, { name: "发票抬头" }]} />
            <div className="invoice-header-table">
                <ProTable
                    key={revision}
                    title="发票抬头"
                    rowKey="id"
                    columns={columns}
                    request={request}
                    header={() => (
                        <Button type="primary" onClick={() => navigate("/me/invoice/add")}>
                            新增
                        </Button>
                    )}
                />
            </div>
            <ZhMessageBox
                open={deleteId != null}
                tip="是否删除"
                onCancel={() => setDeleteId(undefined)}
                onSubmit={remove}
            />
        </section>
    );
}
